import React, { useRef, useState } from "react";
import { Printer, X, Download } from "lucide-react";
import { Brand } from "@/components/Layout";
import { ACT_FONDAMENTALES, ACT_SPEC1, ACT_SPEC2, ACT_SPEC3, PRIORITES } from "@/data/fiche";
import { downloadElementAsPdf } from "@/lib/pdf";

const Row = ({ label, value }) => (
  value ? (
    <div className="grid grid-cols-3 gap-4 mb-1.5 text-sm">
      <span className="col-span-1 text-[var(--ink-soft)] uppercase tracking-[0.06em] text-[13px]">{label}</span>
      <span className="col-span-2 text-[var(--ink)] whitespace-pre-wrap">{value}</span>
    </div>
  ) : null
);

const Block = ({ title, children }) => (
  <div className="mb-8 print-break">
    <h3 className="font-serif-title text-xl mb-3 pb-1.5" style={{ color: "var(--ink)", borderBottom: "1px solid var(--gold)" }}>{title}</h3>
    {children}
  </div>
);

const Bullet = ({ children, testid, faded }) => (
  <p className="flex items-start gap-2.5 text-sm mb-1.5" data-testid={testid}>
    <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: faded ? "var(--gold-light)" : "var(--gold)" }} />
    <span>{children}</span>
  </p>
);

const actLines = (options, val, lang) => {
  const items = (val && val.items) || {};
  const rows = options.filter((o) => items[o.key] && items[o.key].checked).map((o) => ({ label: o[lang], note: items[o.key].note }));
  ((val && val.custom) || []).forEach((c) => rows.push({ label: c.label, note: c.note }));
  return rows;
};

export const FichePrint = ({ nom, prenom, data, lang, T, onClose }) => {
  const d = data || {};
  const info = d.info || {};
  const bilan = d.bilan || {};
  const cur = (d.thematiques && d.thematiques.current) || { themes: [], commentaire: "", objectifProchaine: "" };
  const history = (d.thematiques && d.thematiques.history) || [];
  const objItems = (d.objectifs && d.objectifs.items) || [];
  const sessions = d.sessions || [];
  const prioLabel = (k) => (PRIORITES.find((p) => p.key === k) || {})[lang] || k;
  const docRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const name = [nom, prenom].filter(Boolean).join("_") || "fiche";
      await downloadElementAsPdf(docRef.current, `fiche_${name}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  const ActBlock = ({ title, options, val }) => {
    const rows = actLines(options, val, lang);
    if (rows.length === 0 && !(val && val.commentaire)) return null;
    return (
      <Block title={title}>
        {rows.map((r, i) => (
          <Bullet key={i}><span className="font-medium text-[var(--ink)]">{r.label}</span>{r.note ? <span className="text-[var(--ink-soft)]"> — {r.note}</span> : null}</Bullet>
        ))}
        {val && val.commentaire ? <p className="text-sm text-[var(--ink-soft)] italic mt-2">{val.commentaire}</p> : null}
      </Block>
    );
  };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <button onClick={onClose} data-testid="ficheprint-close" className="inline-flex items-center gap-1.5 text-sm text-[var(--ink-soft)] hover:text-[var(--gold)]"><X size={16} /> {T.closeAttest}</button>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => window.print()} data-testid="ficheprint-print" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-all"><Printer size={16} /> {T.printPdf}</button>
          <button onClick={downloadPdf} disabled={downloading} data-testid="ficheprint-download" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60"><Download size={16} /> {T.downloadPdf}</button>
        </div>
      </div>

      <div ref={docRef} className="print-container mx-auto max-w-3xl bg-white px-10 sm:px-14 py-12" data-testid="ficheprint-document">
        <div className="flex items-end justify-between pb-4 mb-10" style={{ borderBottom: "2px solid var(--gold)" }}>
          <div>
            <h2 className="font-serif-title text-3xl mb-1" style={{ color: "var(--gold)" }}>{T.fichePrintTitle}</h2>
            <p className="text-lg text-[var(--ink)]">{[prenom, nom].filter(Boolean).join(" ") || T.unnamed}</p>
          </div>
          <div className="text-right"><Brand /></div>
        </div>

        <Block title={T.s1}>
          <Row label={T.nom} value={nom} />
          <Row label={T.prenom} value={prenom} />
          <Row label={T.site} value={info.site} />
          <Row label={T.dispositif} value={info.dispositif} />
          <Row label={T.champLibre} value={info.libre} />
        </Block>

        <Block title={T.s2}>
          <p className="text-sm mb-3 text-[var(--ink-soft)] uppercase tracking-[0.06em] text-[13px]">{T.sessionsRealises} : <strong className="text-[var(--ink)]">{sessions.length}</strong></p>
          {sessions.map((s, i) => (
            <Bullet key={s.id || i}>{s.datetime ? new Date(s.datetime).toLocaleString(lang === "es" ? "es-ES" : "fr-FR") : T.empty}{s.note ? <span className="text-[var(--ink-soft)]"> — {s.note}</span> : null}</Bullet>
          ))}
        </Block>

        <Block title={T.s3}><p className="text-sm text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{d.motif || T.empty}</p></Block>

        <Block title={T.s4}>
          <Row label={T.objGeneral} value={(d.objectifs && d.objectifs.general)} />
          {objItems.map((o, i) => (
            <Bullet key={o.id || i}>{o.therapeutique || T.empty} <span className="text-[var(--ink-soft)]">({T.priorite}: {prioLabel(o.priorite)})</span>{o.activites ? <span className="text-[var(--ink-soft)]"> — {o.activites}</span> : null}</Bullet>
          ))}
        </Block>

        <Block title={T.s5}>
          <Row label={T.selectedThemes} value={cur.themes && cur.themes.length ? cur.themes.join(", ") : ""} />
          <Row label={T.themeComment} value={cur.commentaire} />
          <Row label={T.objectifProchaine} value={cur.objectifProchaine} />
          {history.length > 0 && (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <p className="text-[13px] uppercase tracking-[0.06em] text-[var(--ink-soft)] mb-1.5">{T.themeHistory}</p>
              {history.map((h, i) => (
                <p key={i} className="text-xs text-[var(--ink-soft)] mb-0.5">{new Date(h.archivedAt).toLocaleDateString(lang === "es" ? "es-ES" : "fr-FR")} — {(h.themes || []).join(", ")}</p>
              ))}
            </div>
          )}
        </Block>

        <ActBlock title={T.s6} options={ACT_FONDAMENTALES} val={d.fondamentales} />
        <ActBlock title={T.s7} options={ACT_SPEC1} val={d.specifiques1} />
        <ActBlock title={T.s8} options={ACT_SPEC2} val={d.specifiques2} />
        <ActBlock title={T.s9} options={ACT_SPEC3} val={d.specifiques3} />

        <Block title={T.s10}>
          <p className="text-sm text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{d.recap || T.empty}</p>
        </Block>

        <Block title={T.s11}>
          <Row label={T.evolution} value={bilan.evolution} />
          <Row label={T.progres} value={bilan.progres} />
          <Row label={T.difficultes} value={bilan.difficultes} />
          <Row label={T.objectifsPoursuivre} value={bilan.objectifsPoursuivre} />
          <Row label={T.recommandations} value={bilan.recommandations} />
          <Row label={T.prochaineEtape} value={bilan.prochaineEtape} />
        </Block>
      </div>
    </div>
  );
};
